import React, { useEffect, createElement, Component } from 'react';
import { useLanguage } from '../LanguageContext';
export const Seo = () => {
  const {
    language,
    t
  } = useLanguage();
  useEffect(() => {
    // 1. Set HTML lang attribute
    document.documentElement.lang = language;
    // Helper to set or create meta tags
    const setMetaTag = (nameOrProperty: 'name' | 'property', key: string, content: string) => {
      let element = document.querySelector(`meta[${nameOrProperty}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(nameOrProperty, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    // Helper to set or create link tags
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };
    // 2. Set Document Title
    document.title = t.seo.title;
    // 3. Standard Meta Tags
    setMetaTag('name', 'description', t.seo.description);
    setMetaTag('name', 'keywords', t.seo.keywords);
    setMetaTag('name', 'robots', 'index, follow');
    // Canonical URL (assuming root for this single page)
    const canonicalUrl = window.location.origin + window.location.pathname;
    setLinkTag('canonical', canonicalUrl);
    // Favicon — branded "MR" mark as an inline SVG data URI
    const faviconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="16" fill="#4F46E5"/><text x="50%" y="52%" dominant-baseline="central" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="30" font-weight="700" fill="#ffffff">MR</text></svg>';
    let faviconEl = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (!faviconEl) {
      faviconEl = document.createElement('link');
      faviconEl.setAttribute('rel', 'icon');
      document.head.appendChild(faviconEl);
    }
    faviconEl.setAttribute('type', 'image/svg+xml');
    faviconEl.setAttribute('href', 'data:image/svg+xml,' + encodeURIComponent(faviconSvg));
    // 4. Open Graph Tags
    setMetaTag('property', 'og:title', t.seo.title);
    setMetaTag('property', 'og:description', t.seo.description);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:locale', language === 'en' ? 'en_US' : 'es_ES');
    setMetaTag('property', 'og:image', 'https://cdn.magicpatterns.com/uploads/uB7QoxpLNYttPwcdWiGk2u/WhatsApp_Image_2026-06-14_at_18.56.31.jpg');
    setMetaTag('property', 'og:site_name', 'Mora Rebori Spanish');
    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', t.seo.title);
    setMetaTag('name', 'twitter:description', t.seo.description);
    setMetaTag('name', 'twitter:image', 'https://cdn.magicpatterns.com/uploads/uB7QoxpLNYttPwcdWiGk2u/WhatsApp_Image_2026-06-14_at_18.56.31.jpg');
    // 6. JSON-LD Structured Data
    const jsonLdId = 'seo-json-ld';
    let scriptElement = document.getElementById(jsonLdId) as HTMLScriptElement;
    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = jsonLdId;
      scriptElement.type = 'application/ld+json';
      document.head.appendChild(scriptElement);
    }
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'Person',
        '@id': `${canonicalUrl}#person`,
        name: 'Mora Rebori',
        jobTitle: 'Spanish Teacher',
        image: 'https://cdn.magicpatterns.com/uploads/uB7QoxpLNYttPwcdWiGk2u/WhatsApp_Image_2026-06-14_at_18.56.31.jpg',
        url: canonicalUrl,
        sameAs: [],
        knowsLanguage: ['es', 'en'],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '1',
          bestRating: '5'
        }
      }, {
        '@type': 'Course',
        '@id': `${canonicalUrl}#course`,
        name: t.program.title,
        description: t.seo.description,
        provider: {
          '@id': `${canonicalUrl}#person`
        },
        courseMode: 'online',
        educationalCredentialAwarded: 'Fluency in Spanish',
        offers: {
          '@type': 'Offer',
          category: 'Free Trial Session',
          availability: 'https://schema.org/InStock'
        }
      }, {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${canonicalUrl}#person`
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        author: {
          '@type': 'Person',
          name: t.testimonial.name
        },
        reviewBody: t.testimonial.quote.replace(/^"|"$/g, '')
      }, {
        '@type': 'FAQPage',
        mainEntity: t.faq.items.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a
          }
        }))
      }]
    };
    scriptElement.textContent = JSON.stringify(structuredData);
    // Cleanup isn't strictly necessary for these global tags as they just update on language change,
    // but good practice to remove JSON-LD on unmount if component is removed.
    return () => {
      // We don't remove meta tags on unmount to keep SEO intact,
      // but we could if this was a multi-page app.
    };
  }, [language, t]);
  return null; // This component doesn't render any visible UI
};